import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import HPButton from "@/components/common/Button";
import BaseSection from "@components/charts/common/base-config/BaseSection";
import {
  useLazyGetDatasetQuestionsQuery,
  useLazyGetDatasetQuestionsListQuery,
} from "@/store/api/dataset-api";
import { useDebounce } from "@/hooks/use-debounce";
import type {
  DatasetQuestionsSearchResponse,
  QuestionDetail,
} from "@/models/Dataset";
import type {
  ScorecardBrand,
  ScorecardQuestionRow,
  ScorecardRowResultType,
  ScorecardSourceQuestionKind,
} from "@/models/Scorecard";
import type { VisualizationBase } from "@models/VisualizationBase";
import { DEFAULT_VISUALIZATION_BASE } from "@models/VisualizationBase";
import { isVisualizationBaseValid } from "@utils/visualization-base-utils";
import {
  getBrandCandidates,
  getMetricResultType,
  getSourceQuestionKind,
  isEligibleQuestionRowQuestionSummary,
  normalizeQuestionType,
  toMatchableCategories,
} from "@/utils/scorecard-utils";
import { areQuestionRowsEqual } from "@/utils/scorecard-row-lifecycle-utils";
import { useGridMetricSelection } from "@components/charts/scorecard-config/hooks/useGridMetricSelection";
import { useQuestionRowBaseSelection } from "@components/charts/scorecard-config/hooks/useQuestionRowBaseSelection";
import { useRowBrandMapping } from "@components/charts/scorecard-config/hooks/useRowBrandMapping";
import BrandMappingStep from "./BrandMappingStep";
import SourceQuestionAccordion from "./SourceQuestionAccordion";
import ScorecardBaseModeSection from "./ScorecardBaseModeSection";
import QuestionRowResponseSelectionSection from "./QuestionRowResponseSelectionSection";

interface QuestionRowBuilderProps {
  datasetId: string | null;
  brands: ScorecardBrand[];
  otherRows: ScorecardQuestionRow[];
  editingRow?: ScorecardQuestionRow | null;
  onCancel: () => void;
  onSave: (row: ScorecardQuestionRow) => void;
}

/**
 * Inline Question-row builder — lives directly in the Rows section (no
 * modal), mirroring how CrosstabConfigDrawer builds up its Row/Column
 * Question selection via an Accordion inside the drawer body. A fresh
 * instance mounts per add/edit session (RowsSection keys it by
 * editingRow?.id), so initial state can just read from `editingRow` directly
 * instead of an open/close prop. Mounted inside the shared RowBuilder shell
 * (heading + Row Source toggle + outer border), so it owns its own fields
 * and Cancel/Save footer only.
 */
const QuestionRowBuilder: React.FC<QuestionRowBuilderProps> = ({
  datasetId,
  brands,
  otherRows,
  editingRow,
  onCancel,
  onSave,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedQuestionId, setSelectedQuestionId] = useState(
    editingRow?.sourceQuestionId ?? "",
  );
  const [questionDetail, setQuestionDetail] = useState<QuestionDetail | null>(
    null,
  );
  const gridMetric = useGridMetricSelection(editingRow);
  const rowBaseSelection = useQuestionRowBaseSelection(
    datasetId,
    editingRow?.id,
    otherRows,
    brands,
    editingRow?.rowBase,
  );
  const [label, setLabel] = useState(editingRow?.label ?? "");
  const [visualizationBase, setVisualizationBase] = useState<VisualizationBase>(
    editingRow?.visualizationBase ?? DEFAULT_VISUALIZATION_BASE,
  );
  const [labelTouched, setLabelTouched] = useState(!!editingRow);
  // Start collapsed (showing just the chosen question) when editing an
  // existing row — only start expanded (search visible) when adding new.
  const [questionAccordionExpanded, setQuestionAccordionExpanded] = useState(
    !editingRow,
  );

  const observerTarget = useRef<HTMLDivElement>(null);
  const debouncedSearch = useDebounce(searchTerm, 400);

  const [triggerQuestions, { data: questionsData, isFetching }] =
    useLazyGetDatasetQuestionsQuery();
  const [fetchQuestionDetails, { isFetching: isFetchingDetails }] =
    useLazyGetDatasetQuestionsListQuery();

  // Restore the source question's detail once, when editing an existing row.
  // Mirrors handleQuestionSelect's request shape exactly (search_query +
  // dataset_element_id together) — dataset_element_id alone isn't reliably
  // enough for the backend to return the match.
  useEffect(() => {
    if (!editingRow || !datasetId) return;
    fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: editingRow.sourceQuestionLabel,
      dataset_element_id: editingRow.sourceQuestionId,
      expanded: true,
    })
      .unwrap()
      .then((result) => {
        const detail = result.questions.find(
          (q) => q.datasetElementId === editingRow.sourceQuestionId,
        );
        setQuestionDetail(detail ?? null);
        if (detail) gridMetric.restoreRollupCodes(detail.values);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Skips the list fetch while the accordion is collapsed — editing an
  // existing row starts collapsed (just showing the already-chosen question),
  // so there's no need to hit /questions until the researcher actually
  // expands it to search for a different one.
  useEffect(() => {
    if (!datasetId || !questionAccordionExpanded) return;
    triggerQuestions({
      id: datasetId,
      page: 1,
      page_size: 50,
      question_type: ["SINGLE", "MULTI", "FLOAT", "NUMERIC"],
      multidimensional: true,
      search_query: debouncedSearch || undefined,
    });
  }, [datasetId, debouncedSearch, questionAccordionExpanded, triggerQuestions]);

  const eligibleQuestions = useMemo(
    () => (questionsData?.questions ?? []).filter(isEligibleQuestionRowQuestionSummary),
    [questionsData],
  );

  const sourceQuestionKind: ScorecardSourceQuestionKind | null = useMemo(
    () => (questionDetail ? getSourceQuestionKind(questionDetail) : null),
    [questionDetail],
  );

  const resultType: ScorecardRowResultType | null = useMemo(
    () => (questionDetail ? getMetricResultType(questionDetail) : null),
    [questionDetail],
  );

  const existingBaseRow =
    resultType === "percentage" && rowBaseSelection.mode === "existing_question_row"
      ? otherRows.find((r) => r.id === rowBaseSelection.existingRowId)
      : undefined;

  const responseCategories = useMemo(
    () =>
      questionDetail ? toMatchableCategories(getBrandCandidates(questionDetail)) : [],
    [questionDetail],
  );

  const { mappings, resetForNewQuestion, onManualBind, onMarkUnavailable, onRestoreMapping } =
    useRowBrandMapping(brands, responseCategories, editingRow);

  const handleQuestionSelect = async (
    question: DatasetQuestionsSearchResponse,
  ) => {
    setSelectedQuestionId(question.datasetElementId);
    setQuestionDetail(null);
    gridMetric.resetForNewQuestion();
    resetForNewQuestion();
    setLabelTouched(false);
    if (!datasetId) return;

    const result = await fetchQuestionDetails({
      id: datasetId,
      page: 1,
      page_size: 10,
      search_query: question.label,
      dataset_element_id: question.datasetElementId,
      expanded: true,
    }).unwrap();
    const detail = result.questions.find(
      (q) => q.datasetElementId === question.datasetElementId,
    );
    setQuestionDetail(detail ?? null);
    if (detail && getSourceQuestionKind(detail) === "one_dimensional") {
      setLabel(detail.title);
    }
    setQuestionAccordionExpanded(false);
  };

  // Both handlers return/take the resolved display label so the row's own
  // label can be prepopulated — only when the researcher hasn't edited it.
  const handleGridValueChange = (valueId: string) => {
    const valueLabel = gridMetric.handleGridValueChange(
      valueId,
      questionDetail?.values ?? [],
    );
    if (valueLabel && !labelTouched) setLabel(valueLabel);
  };

  const handleRollupNameChange = (name: string) => {
    gridMetric.setRollupName(name);
    if (!labelTouched) setLabel(name);
  };

  const isGridReady = gridMetric.isGridReady(sourceQuestionKind);
  const canSave =
    !!questionDetail &&
    !!resultType &&
    isGridReady &&
    label.trim().length > 0 &&
    isVisualizationBaseValid(visualizationBase) &&
    gridMetric.isRollupValid(questionDetail?.values ?? []) &&
    (resultType !== "percentage" || rowBaseSelection.isValid());

  // Built on every render (cheap — just object construction) so it can
  // double as both the save payload and the input to the dirty-check below.
  const buildRow = (): ScorecardQuestionRow | null => {
    if (!questionDetail || !sourceQuestionKind || !resultType) return null;
    const { valueFocus, metricValueLabel, metricValueCode, rollup, rollupValueIds } =
      gridMetric.buildSaveFields(questionDetail, sourceQuestionKind, editingRow?.rollup?.id);

    return {
      id: editingRow?.id ?? crypto.randomUUID(),
      type: "question",
      label: label.trim(),
      resultType,
      sourceQuestionId: questionDetail.datasetElementId,
      sourceQuestionLabel: questionDetail.label,
      sourceQuestionTitle: questionDetail.title,
      sourceQuestionType: normalizeQuestionType(questionDetail.type) as
        | "SINGLE"
        | "MULTIPLE"
        | "NUMBER"
        | "FLOAT",
      sourceQuestionKind: sourceQuestionKind,
      valueFocus,
      metricValueLabel,
      metricValueCode,
      rollup,
      rollupValueIds,
      brandMappings: mappings,
      responseCategories,
      visualizationBase,
      rowBase: resultType === "percentage" ? rowBaseSelection.buildRowBase() : null,
      rowBaseNeedsAttention: false,
    };
  };

  const pendingRow = buildRow();

  // Only relevant when editing an existing row — nothing to compare a
  // brand-new row against, so it's never blocked by this.
  const isUnchanged =
    !!editingRow && !!pendingRow && areQuestionRowsEqual(pendingRow, editingRow);

  const handleSave = () => {
    if (!pendingRow) return;
    onSave(pendingRow);
  };

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <SourceQuestionAccordion
          label="Source Question*"
          expanded={questionAccordionExpanded}
          onExpandedChange={setQuestionAccordionExpanded}
          selectedQuestionLabel={
            questionDetail?.label ?? editingRow?.sourceQuestionLabel
          }
          selectedQuestionTitle={
            questionDetail?.title ?? editingRow?.sourceQuestionTitle
          }
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          eligibleQuestions={eligibleQuestions}
          selectedQuestionId={selectedQuestionId}
          onQuestionSelect={handleQuestionSelect}
          isFetching={isFetching}
          observerTarget={observerTarget}
          noResultsMessage="No compatible questions found. A Row needs a Single, Multiple, or Single grid question (Percentage) with at least two brand-shaped response options, or a Number/Float question (Mean) with at least two brand-shaped variables."
        />
      </Box>

      {questionDetail && resultType && (
        <QuestionRowResponseSelectionSection
          questionDetail={questionDetail}
          resultType={resultType}
          sourceQuestionKind={sourceQuestionKind}
          isGridReady={isGridReady}
          gridMetric={gridMetric}
          onGridValueChange={handleGridValueChange}
          onRollupNameChange={handleRollupNameChange}
          label={label}
          onLabelChange={(value) => {
            setLabel(value);
            setLabelTouched(true);
          }}
        />
      )}

      {questionDetail && isGridReady && (
        <Box sx={{ mb: 2 }}>
          {resultType === "percentage" ? (
            <ScorecardBaseModeSection
              datasetId={datasetId}
              base={visualizationBase}
              onChange={setVisualizationBase}
              rowBaseSelection={rowBaseSelection}
            />
          ) : (
            // Mean rows (Number/Float questions) aren't eligible Percentage
            // Question rows, so Scorecard Row Base isn't offered — falls
            // back to the generic 3-mode Base.
            <BaseSection
              datasetId={datasetId}
              base={visualizationBase}
              onChange={setVisualizationBase}
            />
          )}
        </Box>
      )}

      {questionDetail && isGridReady && (
        <BrandMappingStep
          brands={brands}
          mappings={mappings}
          responseCategories={responseCategories}
          onManualBind={onManualBind}
          onMarkUnavailable={onMarkUnavailable}
          onRestoreMapping={onRestoreMapping}
          existingRowBaseLabel={existingBaseRow?.label}
          existingBaseRow={existingBaseRow}
        />
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <HPButton variant="outlined" size="small" className="w-full" onClick={onCancel}>
            Cancel
          </HPButton>
        </Box>
        <Box sx={{ flex: 1 }}>
          <HPButton
            variant="contained"
            size="small"
            className="w-full"
            onClick={handleSave}
            disabled={!canSave || isFetchingDetails || isUnchanged}
          >
            {editingRow ? "Apply changes" : "Add row"}
          </HPButton>
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionRowBuilder;
